// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Byte splicing.
 *
 * The contract this file exists to keep: **`cleaned` is byte-identical to the input except for the
 * spans charsight was asked to change.** Not "semantically equivalent", not "the same after
 * normalisation" — identical.
 *
 * The naive implementation is `new TextEncoder().encode(cleanUp(new TextDecoder().decode(bytes)))`,
 * and it is wrong in at least four ways that all fail silently:
 *
 *   · every invalid byte becomes U+FFFD, so a file with one stray Latin-1 byte comes back with
 *     three different bytes there;
 *   · a UTF-8 BOM either disappears or is duplicated, depending on the decoder's mood;
 *   · anything relying on the exact bytes — a checksum, a signature, a `git` blob hash of a file
 *     you only wanted to strip a zero-width from — is destroyed;
 *   · and on a 200 MB file it allocates the whole thing twice as a JS string.
 *
 * So: detection reads a decoded *copy*, and the splice happens on the *original* bytes.
 */

export interface Edit {
  /** Byte offset, inclusive. */
  start: number;
  /** Byte offset, exclusive. */
  end: number;
  /** Bytes to put there. Empty means delete. */
  replacement: Uint8Array;
}

const EMPTY = new Uint8Array(0);

/**
 * Apply edits to a buffer.
 *
 * Edits must not overlap and must be within bounds; both throw rather than corrupting the output,
 * because a splicer that silently drops a conflicting edit produces a file that looks cleaned and
 * is not. Order does not matter — they are sorted here.
 *
 * With no edits the SAME object is returned, not a copy. Callers rely on that to skip writing a
 * file that did not change.
 */
export function spliceBytes(source: Uint8Array, edits: readonly Edit[]): Uint8Array {
  if (edits.length === 0) return source;

  const sorted = [...edits].sort((a, b) => a.start - b.start || a.end - b.end);

  let delta = 0;
  let prevEnd = -1;
  for (const e of sorted) {
    if (e.start < 0 || e.end > source.length || e.end < e.start) {
      throw new RangeError(`edit ${e.start}..${e.end} is out of bounds for ${source.length} bytes`);
    }
    if (e.start < prevEnd) {
      throw new RangeError(`edit ${e.start}..${e.end} overlaps the previous edit ending at ${prevEnd}`);
    }
    prevEnd = e.end;
    delta += e.replacement.length - (e.end - e.start);
  }

  const out = new Uint8Array(source.length + delta);
  let read = 0;
  let write = 0;
  for (const e of sorted) {
    if (e.start > read) {
      out.set(source.subarray(read, e.start), write);
      write += e.start - read;
    }
    if (e.replacement.length) {
      out.set(e.replacement, write);
      write += e.replacement.length;
    }
    read = e.end;
  }
  if (read < source.length) out.set(source.subarray(read), write);
  return out;
}

/** A deletion. */
export function deletion(start: number, end: number): Edit {
  return { start, end, replacement: EMPTY };
}

/** Byte-for-byte equality, for tests and for the "nothing changed" check. */
export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
